import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, ArrowLeft, Eye, FileDown } from "lucide-react";
import { downloadQuotePdf } from "../lib/quoteExport";
import { api } from "../lib/api";
import ImportFromCatalog, { itemToQuoteLine, groupToQuoteGroup } from "../components/ImportFromCatalog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import PreviewModal from "../components/PreviewModal";
import RevisionBanner from "../components/RevisionBanner";
import RevisionHistory from "../components/RevisionHistory";
import { emptyQuote, quoteFromApi, editorTotals, buildQuotePayload, pickPay, emptyItem, ProBadge, qtyError, taxFrom, taxSummary } from "../components/editor/editorShared";
import ClientSection from "../components/editor/ClientSection";
import MetadataSection from "../components/editor/MetadataSection";
import LineItemsSection from "../components/editor/LineItemsSection";
import PackageGroupsSection from "../components/editor/PackageGroupsSection";
import PaymentSettingsSection from "../components/editor/PaymentSettingsSection";
import LivePreviewAside from "../components/editor/LivePreviewAside";
import AcceptedQuoteView from "../components/editor/AcceptedQuoteView";
import UpgradeDialog from "../components/editor/UpgradeDialog";
import SaveToCatalogDialog from "../components/editor/SaveToCatalogDialog";


export default function QuoteEditor() {
  const { id } = useParams();
  const editId = id && id !== "new" ? id : null;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    tax_regime: "forfettario",
    previdenza: "nessuno",
    ritenuta_acconto: false,
    addebito_bollo: false,
  });
  const [q, setQ] = useState(emptyQuote);
  const [savedTax, setSavedTax] = useState(null);
  const [savedProfileTax, setSavedProfileTax] = useState(null);
  const [refreshTax, setRefreshTax] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [revision, setRevision] = useState(null);
  const [revHistory, setRevHistory] = useState([]);
  const draftKey = `qf_quote_draft_${editId || "new"}`;
  const originalRef = useRef(null);
  const [draftInfo, setDraftInfo] = useState(null);

  const [importOpen, setImportOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState("");
  const proGate = (feature) => setUpgradeOpen(`${feature} è una funzione PRO, inclusa nei piani Pro e Fondatore. Passa a Pro per sbloccarla insieme a preventivi illimitati e PDF White-Label.`);
  const [categories, setCategories] = useState([]);
  const [catSave, setCatSave] = useState(null); // {kind, payload, category}
  const [progressEnabled, setProgressEnabled] = useState(false);
  const [progress, setProgress] = useState([]);
  const [isPro, setIsPro] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [manualOverall, setManualOverall] = useState(null);
  const [payInfo, setPayInfo] = useState(null);

  useEffect(() => {
    if (loading || q.status === "accettato" || originalRef.current == null) return;
    const t = setTimeout(() => {
      const cur = JSON.stringify(q);
      if (cur === originalRef.current) localStorage.removeItem(draftKey);
      else localStorage.setItem(draftKey, JSON.stringify({ q, at: Date.now() }));
    }, 400);
    return () => clearTimeout(t);
  }, [q, loading, draftKey]);

  const discardDraft = () => {
    localStorage.removeItem(draftKey);
    setQ(JSON.parse(originalRef.current));
    setDraftInfo(null);
    toast.success("Modifiche annullate");
  };

  useEffect(() => {
    let base = emptyQuote();
    (async () => {
      try {
        const pr = await api.get("/profile");
        setProfile(pr.data);
        const suspended = pr.data.subscription_status === "suspended";
        setIsPro(["pro", "founder", "beta"].includes(pr.data.piano_abbonamento) && !suspended);
        if (suspended && !editId) setUpgradeOpen("Il tuo abbonamento è in pausa. Riattivalo per creare nuovi preventivi.");
      } catch (e) { /* profilo di default già in stato */ }
      try {
        const c = await api.get("/catalog/categories");
        setCategories(c.data);
      } catch (e) { /* noop */ }
      if (editId) {
        try {
          const { data } = await api.get(`/quotes/${editId}`);
          base = quoteFromApi(data);
          setQ(base);
          if (data.tax_settings) setSavedTax(taxFrom(data.tax_settings));
          if (data.profile_tax) setSavedProfileTax(taxFrom(data.profile_tax));
          setRevision(data.status === "modifica_richiesta" ? data.revision_request : null);
          setRevHistory(data.revision_history || []);
          setProgressEnabled(!!data.progress_enabled);
          setManualOverall(data.progress_overall_manual ?? null);
          setPayInfo(pickPay(data));
          setProgress(Array.isArray(data.progress) && data.progress.length
            ? data.progress
            : (data.items || []).map((it) => ({ item_id: it.id, title: it.title, status: "da_iniziare", percent: 0, note: "" })));
        } catch (e) {
          toast.error("Preventivo non trovato");
          navigate("/dashboard");
          return;
        }
      }
      originalRef.current = JSON.stringify(base);
      if (base.status !== "accettato") {
        try {
          const d = JSON.parse(localStorage.getItem(draftKey) || "null");
          if (d?.q && JSON.stringify({ ...d.q, status: base.status }) !== originalRef.current) {
            setQ({ ...d.q, status: base.status });
            setDraftInfo({ at: d.at });
            toast.info("Bozza ripristinata: abbiamo recuperato le modifiche non salvate");
          }
        } catch (e) { localStorage.removeItem(draftKey); }
      }
      setLoading(false);
    })();
  }, [editId, draftKey, navigate]);

  const set = (k, v) => setQ((s) => ({ ...s, [k]: v }));
  const setClient = (k, v) => setQ((s) => ({ ...s, client: { ...s.client, [k]: v } }));

  const updateItem = (idx, patch) =>
    setQ((s) => ({ ...s, items: s.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItem = () => setQ((s) => ({ ...s, items: [...s.items, emptyItem()] }));
  const removeItem = (idx) => setQ((s) => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));

  const emptyPackage = (def = false) => ({
    id: crypto.randomUUID(), name: "", subtitle: "", features: [], price: 0,
    discount_type: "none", discount_value: 0, work_days: 0, iva_rate: 22,
    recommended: false, default_selected: def,
  });
  const addGroup = () =>
    setQ((s) => ({
      ...s,
      groups: [...s.groups, {
        id: crypto.randomUUID(), title: "", description: "",
        packages: [emptyPackage(true), emptyPackage(false)],
      }],
    }));
  const removeGroup = (gi) => setQ((s) => ({ ...s, groups: s.groups.filter((_, i) => i !== gi) }));
  const updateGroup = (gi, patch) =>
    setQ((s) => ({ ...s, groups: s.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const addPackage = (gi) =>
    setQ((s) => ({
      ...s,
      groups: s.groups.map((g, i) =>
        i === gi && g.packages.length < 4 ? { ...g, packages: [...g.packages, emptyPackage(false)] } : g
      ),
    }));
  const removePackage = (gi, pi) =>
    setQ((s) => ({
      ...s,
      groups: s.groups.map((g, i) =>
        i === gi && g.packages.length > 2 ? { ...g, packages: g.packages.filter((_, j) => j !== pi) } : g
      ),
    }));
  const updatePackage = (gi, pi, patch) =>
    setQ((s) => ({
      ...s,
      groups: s.groups.map((g, i) =>
        i === gi ? { ...g, packages: g.packages.map((p, j) => (j === pi ? { ...p, ...patch } : p)) } : g
      ),
    }));
  const setDefaultPackage = (gi, pi) =>
    setQ((s) => ({
      ...s,
      groups: s.groups.map((g, i) =>
        i === gi ? { ...g, packages: g.packages.map((p, j) => ({ ...p, default_selected: j === pi })) } : g
      ),
    }));

  const handleImport = (importedItems, importedGroups) => {
    const newItems = (importedItems || []).map(itemToQuoteLine);
    let newGroups = (importedGroups || []).map(groupToQuoteGroup);
    if (!isPro && newGroups.length) { newGroups = []; proGate("I Gruppi di Pacchetti (Tier)"); }
    setQ((s) => ({ ...s, items: [...s.items, ...newItems], groups: [...s.groups, ...newGroups] }));
    const n = newItems.length + newGroups.length;
    if (n > 0) toast.success(`${n} element${n === 1 ? "o" : "i"} importat${n === 1 ? "o" : "i"} dal listino`);
  };

  const openSaveItem = (it) =>
    setCatSave({
      kind: "item",
      category: categories[0]?.name || "",
      title: it.title,
      payload: {
        title: it.title, description: it.description, item_type: it.item_type,
        unit_price: Number(it.unit_price) || 0, unit: it.unit || "corpo", quantity: Number(it.quantity) || 1,
        min_qty: Number(it.min_qty) || 1, max_qty: Number(it.max_qty) || 10,
        enabled_default: it.enabled_default !== false, work_days: Number(it.work_days) || 0,
        discount_type: it.discount_type || "none", discount_value: Number(it.discount_value) || 0,
        iva_rate: it.iva_rate == null ? 22 : Number(it.iva_rate),
      },
    });

  const openSaveGroup = (g) =>
    setCatSave({
      kind: "group",
      category: categories[0]?.name || "",
      title: g.title,
      payload: {
        title: g.title, description: g.description,
        packages: (g.packages || []).map((p) => ({
          name: p.name || "", subtitle: p.subtitle || "",
          features: Array.isArray(p.features) ? p.features.filter(Boolean)
            : String(p.features || "").split("\n").map((x) => x.trim()).filter(Boolean),
          price: Number(p.price) || 0, discount_type: p.discount_type || "none",
          discount_value: Number(p.discount_value) || 0, work_days: Number(p.work_days) || 0,
          iva_rate: p.iva_rate == null ? 22 : Number(p.iva_rate),
          recommended: !!p.recommended, default_selected: !!p.default_selected,
        })),
      },
    });

  const confirmSaveToCatalog = async () => {
    if (!catSave.title?.trim()) return toast.error("La voce deve avere un titolo prima di salvarla");
    try {
      const typed = (catSave.category || "").trim();
      const known = categories.find((c) => c.name.toLowerCase() === typed.toLowerCase());
      if (typed && !known) await api.post("/catalog/categories", { name: typed });
      const body = { ...catSave.payload, category: known ? known.name : typed };
      if (catSave.kind === "item") await api.post("/catalog/items", body);
      else await api.post("/catalog/groups", body);
      toast.success("Servizio salvato nel tuo Listino!");
      setCatSave(null);
      try {
        const c = await api.get("/catalog/categories");
        setCategories(c.data);
      } catch (e) { /* noop */ }
    } catch (e) {
      toast.error("Errore nel salvataggio nel listino");
    }
  };

  const taxSettings = useMemo(() => (editId && savedTax && !refreshTax ? savedTax : taxFrom(profile)), [editId, savedTax, refreshTax, profile]);
  const taxChanged = !!(editId && savedTax && savedProfileTax && !refreshTax && JSON.stringify(savedTax) !== JSON.stringify(savedProfileTax));
  const totals = useMemo(() => editorTotals(q, taxSettings), [q, taxSettings]);
  const cur = q.currency || "EUR";

  const saveProgress = async () => {
    setSavingProgress(true);
    try {
      const { data } = await api.put(`/quotes/${editId}/progress`, { progress_enabled: q.progress_enabled, progress, progress_overall: manualOverall });
      toast.success(`Avanzamento lavori salvato (${data.overall}%)`);
      const fresh = await api.get(`/quotes/${editId}`);
      setPayInfo(pickPay(fresh.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    }
    setSavingProgress(false);
  };

  const sendReminder = async (stage) => {
    try {
      const { data } = await api.post(`/quotes/${editId}/payment-reminder`, { stage });
      toast.success(data.sent ? "Sollecito inviato al cliente via email" : "Sollecito registrato (email cliente non disponibile)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    }
  };


  const buildPayload = (asDraft = false) => ({ ...buildQuotePayload(q, asDraft), refresh_tax: refreshTax });

  const save = async (asDraft = false, stay = false) => {
    const badIdx = q.items.findIndex((it) => qtyError(it));
    if (badIdx >= 0) return toast.error(`Voce ${badIdx + 1}: ${qtyError(q.items[badIdx])}`);
    if (!q.title.trim()) return toast.error("Inserisci il titolo del progetto");
    if (!q.client.name.trim()) return toast.error("Inserisci il nome del cliente");
    setSaving(true);
    try {
      const payload = buildPayload(asDraft);
      if (editId) await api.put(`/quotes/${editId}`, payload);
      else await api.post("/quotes", payload);
      localStorage.removeItem(draftKey);
      if (refreshTax) { setSavedTax(taxFrom(profile)); setRefreshTax(false); }
      if (stay) { originalRef.current = JSON.stringify(q); setDraftInfo(null); setSaving(false); return true; }
      toast.success(editId ? "Preventivo aggiornato!" : "Preventivo creato!");
      navigate("/dashboard");
    } catch (e) {
      if (e?.response?.status === 402) {
        setUpgradeOpen(e?.response?.data?.detail || "Hai raggiunto il limite di 3 preventivi al mese del Piano Starter gratuito.");
      } else {
        const d = e?.response?.data?.detail;
        toast.error(Array.isArray(d) ? String(d[0]?.msg || "").replace(/^Value error, /, "") || "Errore nel salvataggio" : d || "Errore nel salvataggio");
      }
    }
    setSaving(false);
    return false;
  };

  if (loading)
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );

  if (editId && q.status === "accettato") {
    return <AcceptedQuoteView {...{ cur, editId, isPro, manualOverall, navigate, payInfo, progress, q, revHistory, saveProgress, savingProgress, sendReminder, set, setManualOverall, setPayInfo, setProgress }} />;
  }

  return (
    <div className="max-w-full overflow-x-hidden">
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="editor-back-button">
            <ArrowLeft style={{ width: 18, height: 18 }} />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 truncate">
              {editId ? "Modifica Preventivo" : "Nuovo Preventivo"}
            </h1>
            <p className="text-sm text-slate-500" data-testid="editor-tax-line">Regime: {taxSettings.tax_regime === "ordinario" ? "Ordinario · IVA 22%" : "Forfettario"}{taxSettings.previdenza !== "nessuno" ? ` · Contributo ${taxSettings.previdenza === "cassa" ? taxSettings.cassa_rate : 4}%` : ""}{taxSettings.tax_regime === "ordinario" && taxSettings.ritenuta_acconto ? " · Ritenuta 20%" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {editId && (
            <Button variant="outline" data-testid="quote-editor-pdf-button" onClick={() => downloadQuotePdf(editId).catch(() => toast.error("Esportazione PDF non riuscita"))} className="h-10 font-semibold" title="Scarica il PDF dell'ultima versione salvata">
              <FileDown style={{ width: 16, height: 16 }} className="sm:mr-2" /><span className="hidden sm:inline">PDF</span>
            </Button>
          )}
          <Button variant="outline" data-testid="quote-editor-preview-button" onClick={() => setPreviewOpen(true)} className="h-10 font-semibold">
            <Eye style={{ width: 16, height: 16 }} className="sm:mr-2" /><span className="hidden sm:inline">Anteprima</span>
          </Button>
          <Button data-testid="quote-editor-save-button" onClick={() => save(false)} disabled={saving} className="h-10 font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin sm:mr-2" /> : <Save style={{ width: 16, height: 16 }} className="sm:mr-2" />}
            <span className="hidden sm:inline">Salva Preventivo</span>
          </Button>
        </div>
      </div>
      {revision && (
        <RevisionBanner revision={revision} items={q.items} quoteId={editId}
          onApply={(qtys) => setQ((s) => ({ ...s, items: s.items.map((it) => (qtys[it.id] !== undefined ? { ...it, quantity: qtys[it.id] } : it)) }))}
          onSaveBeforeResend={() => save(false, true)}
          onRejected={async () => {
            const { data } = await api.get(`/quotes/${editId}`);
            localStorage.removeItem(draftKey);
            setRevision(null);
            setRevHistory(data.revision_history || []);
            setQ((s) => { const n = { ...s, items: data.items || [], status: data.status }; originalRef.current = JSON.stringify(n); return n; });
            setDraftInfo(null);
          }}
          onResent={() => { setRevision(null); setQ((s) => ({ ...s, status: "inviato" })); api.get(`/quotes/${editId}`).then(({ data }) => setRevHistory(data.revision_history || [])); }} />
      )}
      <RevisionHistory history={revHistory} cur={cur} collapsible className="mb-6" />
      {taxChanged && (q.status === "bozza" ? (
        <div data-testid="tax-changed-banner" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-amber-900">Hai cambiato le impostazioni fiscali dopo aver creato questo preventivo. Qui valgono ancora: <span className="font-semibold" data-testid="tax-changed-summary">{taxSummary(savedTax)}</span>.</p>
          <Button size="sm" variant="outline" data-testid="tax-refresh-button" onClick={() => setRefreshTax(true)} className="border-amber-300">Aggiorna alle impostazioni attuali</Button>
        </div>
      ) : (
        <p data-testid="tax-locked-note" className="mb-6 text-sm text-slate-500">Le impostazioni fiscali restano quelle dell'invio. Per cambiarle crea una nuova versione.</p>
      ))}
      {draftInfo && (
        <div data-testid="draft-restored-banner" className="mb-6 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-sky-900"><span className="font-semibold">Bozza ripristinata</span> · modifiche non salvate del {new Date(draftInfo.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</p>
          <Button size="sm" variant="outline" data-testid="draft-discard-button" onClick={discardDraft} className="border-sky-300">Annulla modifiche</Button>
        </div>
      )}
      <PreviewModal open={previewOpen} onOpenChange={setPreviewOpen} buildPayload={buildPayload} quoteId={editId} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8 space-y-6 min-w-0">
          {/* Client */}
          <ClientSection {...{ q, setClient }} />

          {/* Metadata */}
          <MetadataSection {...{ q, set }} />

          {/* Line items */}
          <LineItemsSection {...{ addItem, openSaveItem, taxSettings, q, removeItem, setImportOpen, updateItem }} />

          {/* Package groups (Tier) */}
          <PackageGroupsSection {...{ addGroup, addPackage, isPro, openSaveGroup, proGate, q, removeGroup, removePackage, set, setDefaultPackage, setImportOpen, updateGroup, updatePackage }} />

          {/* Payment */}
          <PaymentSettingsSection {...{ cur, isPro, proGate, q, set, totals }} />
        </div>

        {/* Live preview */}
        <LivePreviewAside {...{ cur, totals }} />
      </div>

      <ImportFromCatalog open={importOpen} onOpenChange={setImportOpen} onImport={handleImport} />

      <div data-testid="show-progress-option" className="mt-8 bg-card border border-slate-200/80 rounded-xl p-5 shadow-sm flex items-start gap-3">
        <input type="checkbox" data-testid="show-progress-checkbox" checked={isPro && q.progress_enabled !== false}
          onChange={(e) => (isPro ? set("progress_enabled", e.target.checked) : proGate("L'Avanzamento Lavori"))} className="rounded border-slate-300 mt-0.5 h-4 w-4 accent-indigo-600" />
        <label className="text-sm text-slate-700 cursor-pointer" onClick={() => (isPro ? set("progress_enabled", !(q.progress_enabled !== false)) : proGate("L'Avanzamento Lavori"))}>
          <span className="font-medium text-slate-900 inline-flex items-center gap-2">Mostra stato di avanzamento lavori al cliente dopo l'accettazione{!isPro && <ProBadge testid="progress-option-pro-badge" onClick={() => proGate("L'Avanzamento Lavori")} />}</span>
          <span className="block text-slate-500">Il cliente vedrà una barra di avanzamento aggiornabile da te sul link pubblico del preventivo accettato.</span>
        </label>
      </div>


      {/* Bottom save bar */}
      <div data-testid="editor-bottom-save-bar" className="sticky bottom-0 mt-8 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 py-4 bg-background/90 backdrop-blur border-t border-border flex flex-col sm:flex-row items-center justify-end gap-3">
        <Button data-testid="quote-editor-save-bottom" onClick={() => save(false)} disabled={saving} className="w-full sm:w-auto font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save style={{ width: 16, height: 16 }} className="mr-2" />}
          Salva Preventivo
        </Button>
      </div>


      <UpgradeDialog {...{ navigate, setUpgradeOpen, upgradeOpen }} />
      <SaveToCatalogDialog {...{ catSave, categories, confirmSaveToCatalog, setCatSave }} />
    </div>
  );
}
